/**
 * PATH: packages/kanban-host/src/kanbanOrgIdResolver.ts
 * FILE_ID: KANBANHOST-KANBANORGIDRESOLVER-001
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/kanbanOrgIdResolver.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/kanbanOrgIdResolver.ts: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 */

/** Minimal structural slice of a session this resolver actually reads.
 *  Deliberately NOT `AuthSessionPayload` (UAT P0-a): the pages call this
 *  with the DI-seam `KanbanAuthSession` (see `_internal/auth/useApiAuth`),
 *  and demanding the full tenant payload type forced unsafe casts at all
 *  four call sites. Both `AuthSessionPayload` and `KanbanAuthSession`
 *  are structurally assignable to this slice. */
export interface KanbanOrgIdSessionSlice {
  /** Only `quotech_org_id` is read; id/slug/name are declared so org
   *  shapes that DON'T carry quotech_org_id (email-hub's standalone
   *  `SessionOrganisation`) still share common properties with this
   *  slice and dodge TS's weak-type rejection — those callers simply
   *  resolve through the runtime/buildtime fallbacks. */
  organisation?: {
    id?: number | null
    slug?: string | null
    name?: string | null
    quotech_org_id?: number | null
  } | null
}


export interface ResolveKanbanOrgIdSources {
  /** Master Quotech id pushed into the runtime store by
   *  ``ApiAuthGate``'s ``applySessionSideEffect`` on every successful
   *  /auth/sso. Read via ``resolveQuotechTenantId()`` in the caller. */
  runtimeId: number | null
  /** Build-time ``VITE_QUOTECH_ORG_ID`` override read at module load
   *  in ``quotechApi.ts``. ``null`` when the build doesn't set it. */
  buildtimeId: number | null
}


/**
 * Pure I→O: given a session payload + the runtime/buildtime fallback
 * values, return the master Quotech org id to use for every kanban
 * call, or ``undefined`` if no source has produced a positive integer.
 *
 * Why pure: the React component re-creates the resolver on every
 * render anyway (we re-read all three sources each time); centralising
 * the precedence here means the rules are unit-testable without a
 * render harness, and a future regression that swaps the order of
 * fallbacks shows up in the test diff instead of being silently
 * shipped behind a hooks-y wrapper.
 */
export function resolveKanbanOrgId(
  session: KanbanOrgIdSessionSlice | null | undefined,
  sources: ResolveKanbanOrgIdSources,
): number | undefined {
  const sessionId = session?.organisation?.quotech_org_id
  if (_isValidOrgId(sessionId)) return sessionId
  if (_isValidOrgId(sources.runtimeId)) return sources.runtimeId
  if (_isValidOrgId(sources.buildtimeId)) return sources.buildtimeId
  return undefined
}


/** Positive integer guard. Treats 0, NaN, negatives, ``null``, and
 *  ``undefined`` as "no value". Guards against a future BE that emits
 *  ``quotech_org_id: 0`` to mean "unmapped" — without this we'd happily
 *  send 0 to the master backend and watch it 404. */
function _isValidOrgId(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
